/**
 * theme/ruin.ts — the first cache dressing layer: an OUTDOOR ruin.
 *
 * `ruin` is the cheap theme because it is **walled by terrain**. Room floors are flat
 * plateaus cut into a mesa; corridors are ramps between them; everything else rises
 * into an impassable ridge within a few metres. There is no ceiling, no interior
 * lighting pass and no enclosed-space camera problem — which is exactly why it is the
 * theme that proves the abstraction before `vault`/`hollow` (Phase 7) pay for geometry.
 *
 * ⚠ THE RIDGES ARE NOT THE COLLISION. They read as impassable, and the slope check
 * mostly agrees, but "mostly" is how players end up outside a level. Every room
 * boundary that is not a corridor mouth, both sides of every corridor, and every closed
 * gate get a REAL box in the {@link BoxColliderField}. The boxes are the truth; the
 * ridge is the picture. `cache-render.test.mts` asserts the boxes, not the slope.
 *
 * GEOMETRY MODEL. Two kinds of walkable feature, both in pad-local metres:
 *   ROOM      an axis-aligned rectangle at a constant plateau height
 *   CORRIDOR  a polyline (room mouth → working-grid cell centres → room mouth) with a
 *             half-width, whose height lerps between its two rooms' plateaus by ARC
 *             LENGTH at the nearest point — a nearest-point-on-polyline field, so
 *             bends stay continuous instead of stepping at the corner.
 * `heightAt` takes the nearest feature (bucketed by working cell), sits on its floor
 * inside it, and blends up to the mesa top outside it. Because the mesa top is a single
 * global altitude, there is no seam along the medial axis between two features at
 * different floor heights.
 *
 * DETERMINISM. Everything derives from the layout plus `rngFromString(site:epoch:ruin)`.
 * No `Math.random`, no clock — dressing the same layout twice yields identical boxes and
 * identical heights (pinned by the render gate).
 *
 * SPACE. `group` is in RENDER space (world − the pad's pole), matching combat-lab: every
 * visual sits on `sphereY(x, z) = √((R+h)² − x² − z²) − R`, never on flat `h`.
 */
import * as THREE from "three";
import {
  BoxColliderField,
  CACHE_PAD_RADIUS,
  CACHE_WALL_PARAMS,
  rngFromString,
  type BoxSpec,
  type CacheLayoutV1,
  type CachePropKind,
  type CacheRole,
  type CacheRoom,
} from "@hyperlight/shared";
// The ONE import ruin.ts takes from the extracted toolkit. `common.ts` was written as an
// extraction BY COPY precisely so this file could stay frozen — but the P6b live-mode sink
// allowance must be ONE number across all three themes, and duplicating it here is how the
// themes drift apart. Nothing else is imported; the copy relationship is otherwise intact.
import { LIVE_SINK_EXTRA_M, LIVE_UNDERPLATE_M, roomFloorHeights } from "./common";

// ── knobs (binding — the render gate reads several of these) ───────────────────

/**
 * Half-width of a corridor, metres.
 *
 * ⚠ ONE SOURCE OF TRUTH — this is `CACHE_WALL_PARAMS.ruin.corridorHalf`, not a literal
 * beside it. The dressing draws the walls you SEE and shared/src/cache/walls.ts draws
 * the walls that stop BULLETS along the same mouth cuts; two copies of the number is
 * two dungeons, and the 2026-08-02 widening is exactly the edit that would have split
 * them. Must stay under the smallest room half-width (role scale 0.6 × the 18 m tier-0
 * cell = 10.8 m wide → 5.4 m half) so a mouth is a real opening in a wall rather than
 * the whole wall.
 */
export const CORRIDOR_HALF = CACHE_WALL_PARAMS.ruin.corridorHalf;
/** Wall thickness, metres. */
export const WALL_T = 0.7;
/** Wall height above its floor, metres — well over a jump-less step-up. */
export const WALL_H = 4.6;
/** Closed-gate barrier height, metres. */
export const GATE_H = 4.2;
/** How far a wall/gate box sinks below its floor so a ramp never opens a gap under it. */
const SINK = 0.4;
/** Height ladder: `FLOOR_LADDER.ruin` in shared/src/cache/layout.ts. */
/** Mesa top above the HIGHEST room floor, metres. */
export const RIDGE_H = 7;
/** Distance from a walkable edge over which the ground climbs to the mesa top, metres.
 *  Under half a working cell, so every non-floor cell is fully ridged. */
export const RIDGE_RUN = 4.5;
/** Ridge detail noise amplitude / wavelength, metres. */
const NOISE_AMP = 0.55;
const NOISE_SCALE = 13;
const NOISE_G = 64;

/** Floor tint per room role — silhouette-level readability, not art. */
const ROLE_COLOR: Record<CacheRole, number> = {
  entrance: 0x2f7f9e,
  transit: 0x4a5361,
  arena: 0x7d6440,
  gate: 0xb0862a,
  key: 0x4f9c5e,
  treasure: 0xc2a132,
  boss: 0x9c3535,
  shortcut: 0x5f5291,
  vista: 0x3f8a72,
};

// ── public shapes ─────────────────────────────────────────────────────────────

/**
 * One solid box in pad-local terms — the single description both the collider
 * ({@link BoxSpec}) and the mesh are derived from, so what you see is what you hit.
 * `along` is the horizontal axis the box is LONG on; thickness runs on the other.
 */
export interface WallBox {
  /** Pad-local centre of the box's base footprint. */
  cx: number;
  cz: number;
  along: "x" | "z";
  /** Half the length along `along`. */
  halfLen: number;
  /** Half the thickness across `along`. */
  halfT: number;
  /** Absolute floor height (above the base sphere) the box stands on. */
  baseH: number;
  /** Full height above `baseH`. */
  height: number;
}

export interface RuinGate {
  /** Index into `layout.edges`. */
  edgeIndex: number;
  box: WallBox;
  open: boolean;
}

export interface RuinDressing {
  readonly layout: CacheLayoutV1;
  /** Pad sphere radius the geometry was built for. */
  readonly radius: number;
  /** Terrain height above the base sphere at pad-local (x, z) — feed to `makePadHost`. */
  heightAt(x: number, z: number): number;
  /** Render-space ground height (the sphereY law) — every visual sits on this. */
  sphereY(x: number, z: number): number;
  /** Current solid-box field. Replaced by {@link setGateOpen}. */
  colliders: BoxColliderField;
  /** Render-space scene content (terrain, walls, gates, props, markers). */
  group: THREE.Group;
  /** Every static wall box, in build order — the lab's collider overlay reads this. */
  readonly walls: readonly WallBox[];
  readonly gates: RuinGate[];
  /** Room floor heights, indexed by room. */
  readonly floorH: readonly number[];
  /**
   * Open/close a gate. Rebuilds the collider field (the field has no removal seam) and
   * returns the NEW one — hand it to `padHost.setColliders`.
   */
  setGateOpen(edgeIndex: number, open: boolean): BoxColliderField;
  dispose(): void;
}

export interface RuinOptions {
  radius?: number;
  /** Build the THREE scene content. Off for headless sweeps (`group` stays empty). */
  visuals?: boolean;
  /**
   * LIVE-ON-A-PLANET mode (P6b) — see `ThemeOptions.flat` in `./common` for the full
   * reasoning. Default false, so the lab and every measured number in
   * `test:cacherender` are unchanged. In short: the dressing does not own the ground
   * live, so room floor terraces are zeroed (the server seeds drones on the datum),
   * the dressing's own ground mesh is not built (the host Planet draws one), and every
   * solid sinks a further {@link LIVE_SINK_EXTRA_M} so the pad planet's sampler relief
   * cannot open a gap under a wall.
   */
  flat?: boolean;
}

// ── internals ─────────────────────────────────────────────────────────────────

const smooth01 = (t: number): number => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));

interface RoomFeat { x0: number; x1: number; z0: number; z1: number; h: number; }
interface CorrFeat {
  /** Flat [x, z, x, z, …] polyline: room-A mouth → cell centres → room-B mouth. */
  pts: number[];
  /** Cumulative arc length at each point. */
  cum: number[];
  len: number;
  /** Floor height at each polyline NODE. Interior nodes carry the SHARED height of
   *  their working cell (see the cell-height pass) — that is what keeps two corridors
   *  that cross from disagreeing about the ground at the crossing. */
  hs: number[];
}

/** Scratch for the nearest-point query (hot path — no allocation). */
const _near = { d: 0, seg: 0, s: 0 };

function nearestOnPoly(c: CorrFeat, x: number, z: number): void {
  let bd = Infinity, bseg = 0, bs = 0;
  for (let i = 0; i + 3 < c.pts.length; i += 2) {
    const ax = c.pts[i], az = c.pts[i + 1], bx = c.pts[i + 2], bz = c.pts[i + 3];
    const ex = bx - ax, ez = bz - az;
    const ll = ex * ex + ez * ez;
    let s = ll > 0 ? ((x - ax) * ex + (z - az) * ez) / ll : 0;
    s = s < 0 ? 0 : s > 1 ? 1 : s;
    const px = ax + ex * s, pz = az + ez * s;
    const dx = x - px, dz = z - pz;
    const d = Math.sqrt(dx * dx + dz * dz);
    if (d < bd) { bd = d; bseg = i >> 1; bs = s; }
  }
  _near.d = bd;
  _near.seg = bseg;
  _near.s = bs;
}

/** Floor height of a corridor at the last {@link nearestOnPoly} result. */
function corrH(c: CorrFeat): number {
  const a = c.hs[_near.seg], b = c.hs[_near.seg + 1];
  return a + (b - a) * _near.s;
}

function sdRect(r: RoomFeat, x: number, z: number): number {
  const dx = Math.max(r.x0 - x, x - r.x1);
  const dz = Math.max(r.z0 - z, z - r.z1);
  if (dx <= 0 && dz <= 0) return Math.max(dx, dz);
  const ox = Math.max(dx, 0), oz = Math.max(dz, 0);
  return Math.sqrt(ox * ox + oz * oz);
}

/** Sides of a room rectangle: 0 = +x, 1 = −x, 2 = +z, 3 = −z. */
const SIDE_DX = [1, -1, 0, 0];
const SIDE_DZ = [0, 0, 1, -1];
function sideOf(dx: number, dz: number): number {
  if (dx > 0) return 0;
  if (dx < 0) return 1;
  return dz > 0 ? 2 : 3;
}

/**
 * Dress a layout as an outdoor ruin.
 *
 * Returns the heightfield the pad host displaces its sphere with, the solid boxes the
 * character collides against, and (unless `visuals: false`) the render-space scene
 * content. Pure in the layout + its seed.
 */
export function dressRuin(layout: CacheLayoutV1, opts: RuinOptions = {}): RuinDressing {
  const R = opts.radius ?? CACHE_PAD_RADIUS;
  const rng = rngFromString(`${layout.site}:${layout.epoch}:ruin`);
  const rooms = layout.rooms;
  const n = rooms.length;

  // ── 1. floor heights ────────────────────────────────────────────────────────
  // The BFS-depth terrace ladder now lives in the LAYOUT (shared/src/cache/layout.ts),
  // so the server seeds a garrison on the same floors the dressing stands on. This call
  // still spends the jitter draws the ladder used to take from this stream — see
  // `roomFloorHeights` in ./common for why that is load-bearing.
  const floorH = roomFloorHeights(layout, rng, opts);
  const SINK_M = opts.flat === true ? SINK + LIVE_SINK_EXTRA_M : SINK;
  let highest = 0;
  for (let i = 0; i < n; i++) highest = Math.max(highest, floorH[i]);
  const ridgeTop = highest + RIDGE_H;

  // ── 2. walkable features ────────────────────────────────────────────────────
  const roomFeats: RoomFeat[] = rooms.map((r, i) => ({
    x0: r.x - r.w / 2, x1: r.x + r.w / 2,
    z0: r.z - r.d / 2, z1: r.z + r.d / 2,
    h: floorH[i],
  }));

  const half = (layout.grid - 1) / 2;
  /** Working-cell index → pad-local metres (same law on both axes — space.ts's `x`/`z`). */
  const cellPos = (c: number): number => (c - half) * layout.cell;
  /** Mouth side per room, one bit per side (see {@link sideOf}). */
  const mouths: number[] = rooms.map(() => 0);

  /** Per-edge polyline geometry, before the shared cell-height pass. */
  const geoms = layout.edges.map((e) => {
    const A = rooms[e.a], B = rooms[e.b];
    const cells = e.cells;
    const firstU = cells.length > 0 ? cells[0][0] : B.cu;
    const firstV = cells.length > 0 ? cells[0][1] : B.cv;
    const lastU = cells.length > 0 ? cells[cells.length - 1][0] : A.cu;
    const lastV = cells.length > 0 ? cells[cells.length - 1][1] : A.cv;
    const sa = sideOf(Math.sign(firstU - A.cu), Math.sign(firstV - A.cv));
    const sb = sideOf(Math.sign(lastU - B.cu), Math.sign(lastV - B.cv));
    mouths[e.a] |= 1 << sa;
    mouths[e.b] |= 1 << sb;
    const halfA = SIDE_DX[sa] !== 0 ? A.w / 2 : A.d / 2;
    const halfB = SIDE_DX[sb] !== 0 ? B.w / 2 : B.d / 2;
    const pts: number[] = [A.x + SIDE_DX[sa] * halfA, A.z + SIDE_DZ[sa] * halfA];
    for (const [cu, cv] of cells) pts.push(cellPos(cu), cellPos(cv));
    pts.push(B.x + SIDE_DX[sb] * halfB, B.z + SIDE_DZ[sb] * halfB);
    const cum: number[] = [0];
    for (let i = 0; i + 3 < pts.length; i += 2) {
      const dx = pts[i + 2] - pts[i], dz = pts[i + 3] - pts[i + 1];
      cum.push(cum[cum.length - 1] + Math.sqrt(dx * dx + dz * dz));
    }
    return { pts, cum, len: cum[cum.length - 1], cells, hA: floorH[e.a], hB: floorH[e.b] };
  });

  // Shared working-cell heights. Two corridors are allowed to CROSS (space.ts only
  // forbids routing through a third ROOM), and when they do they must agree about the
  // ground at the shared cell — otherwise the nearest-feature height field puts a
  // multi-metre step across the crossing, which reads as a cliff in the middle of a
  // corridor. Every corridor through a cell votes with the height its own ramp wants
  // there; the cell takes the mean and every corridor ramps through that.
  const cellVote = new Map<number, { sum: number; n: number }>();
  const cellKey = (cu: number, cv: number): number => cu * 4096 + cv;
  for (const g of geoms) {
    for (let j = 0; j < g.cells.length; j++) {
      const want = g.hA + (g.hB - g.hA) * (g.len > 0 ? g.cum[j + 1] / g.len : 0);
      const k = cellKey(g.cells[j][0], g.cells[j][1]);
      const v = cellVote.get(k);
      if (v === undefined) cellVote.set(k, { sum: want, n: 1 });
      else { v.sum += want; v.n += 1; }
    }
  }
  const corrFeats: CorrFeat[] = geoms.map((g) => {
    const hs: number[] = [g.hA];
    for (const [cu, cv] of g.cells) {
      const v = cellVote.get(cellKey(cu, cv))!;
      hs.push(v.sum / v.n);
    }
    hs.push(g.hB);
    return { pts: g.pts, cum: g.cum, len: g.len, hs };
  });

  // ── 3. bucket index (working-cell grid) ─────────────────────────────────────
  // A query only has to see features within RIDGE_RUN; past that the ground IS the
  // mesa top, so an empty bucket answers `ridgeTop` with no discontinuity.
  const cellM = layout.cell;
  const bucket = new Map<number, number[]>();
  const key = (i: number, j: number): number => (i + 4096) * 16384 + (j + 4096);
  const stamp = (id: number, x0: number, x1: number, z0: number, z1: number): void => {
    const i0 = Math.floor((x0 - RIDGE_RUN) / cellM), i1 = Math.floor((x1 + RIDGE_RUN) / cellM);
    const j0 = Math.floor((z0 - RIDGE_RUN) / cellM), j1 = Math.floor((z1 + RIDGE_RUN) / cellM);
    for (let i = i0; i <= i1; i++) {
      for (let j = j0; j <= j1; j++) {
        const k = key(i, j);
        let list = bucket.get(k);
        if (list === undefined) bucket.set(k, (list = []));
        list.push(id);
      }
    }
  };
  roomFeats.forEach((r, i) => stamp(i, r.x0, r.x1, r.z0, r.z1));
  corrFeats.forEach((c, i) => {
    let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
    for (let p = 0; p < c.pts.length; p += 2) {
      x0 = Math.min(x0, c.pts[p]); x1 = Math.max(x1, c.pts[p]);
      z0 = Math.min(z0, c.pts[p + 1]); z1 = Math.max(z1, c.pts[p + 1]);
    }
    stamp(roomFeats.length + i, x0 - CORRIDOR_HALF, x1 + CORRIDOR_HALF, z0 - CORRIDOR_HALF, z1 + CORRIDOR_HALF);
  });

  // ── 4. ridge detail noise (value noise on a seeded torus) ───────────────────
  const nt = new Float64Array(NOISE_G * NOISE_G);
  for (let i = 0; i < nt.length; i++) nt[i] = rng() * 2 - 1;
  const wrap = (v: number): number => ((v % NOISE_G) + NOISE_G) % NOISE_G;
  const nAt = (i: number, j: number): number => nt[wrap(j) * NOISE_G + wrap(i)];
  const noise = (x: number, z: number): number => {
    const u = x / NOISE_SCALE, v = z / NOISE_SCALE;
    const i0 = Math.floor(u), j0 = Math.floor(v);
    const su = smooth01(u - i0), sv = smooth01(v - j0);
    const a = nAt(i0, j0) + (nAt(i0 + 1, j0) - nAt(i0, j0)) * su;
    const b = nAt(i0, j0 + 1) + (nAt(i0 + 1, j0 + 1) - nAt(i0, j0 + 1)) * su;
    return (a + (b - a) * sv) * NOISE_AMP;
  };

  /** Signed distance to the nearest walkable feature (negative inside). */
  const nearestSd = (x: number, z: number): number => {
    const list = bucket.get(key(Math.floor(x / cellM), Math.floor(z / cellM)));
    if (list === undefined) return RIDGE_RUN * 4;
    let bd = Infinity;
    for (const id of list) {
      let d: number;
      if (id < roomFeats.length) d = sdRect(roomFeats[id], x, z);
      else { nearestOnPoly(corrFeats[id - roomFeats.length], x, z); d = _near.d - CORRIDOR_HALF; }
      if (d < bd) bd = d;
    }
    return bd;
  };

  const heightAt = (x: number, z: number): number => {
    const list = bucket.get(key(Math.floor(x / cellM), Math.floor(z / cellM)));
    if (list === undefined) return ridgeTop + noise(x, z);
    let bd = Infinity, bh = ridgeTop;
    // Depth-weighted blend across OVERLAPPING walkable features. Where only one
    // covers the point this is exactly that feature's floor; where two overlap (a
    // corridor crossing, a corridor's mouth inside its room) it eases between them
    // instead of stepping when the nearest one changes.
    let wsum = 0, hsum = 0;
    for (const id of list) {
      let d: number, h: number;
      if (id < roomFeats.length) {
        const r = roomFeats[id];
        d = sdRect(r, x, z);
        h = r.h;
      } else {
        const c = corrFeats[id - roomFeats.length];
        nearestOnPoly(c, x, z);
        d = _near.d - CORRIDOR_HALF;
        h = corrH(c);
      }
      if (d < bd) { bd = d; bh = h; }
      if (d < 0) { const w = d * d; wsum += w; hsum += w * h; }
    }
    if (bd <= 0) return wsum > 0 ? hsum / wsum : bh;
    const s = smooth01(bd / RIDGE_RUN);
    return bh + (ridgeTop - bh) * s + noise(x, z) * s * s;
  };

  const sphereY = (x: number, z: number): number => {
    const rh = R + heightAt(x, z);
    return Math.sqrt(Math.max(0, rh * rh - x * x - z * z)) - R;
  };

  // ── 5. walls ────────────────────────────────────────────────────────────────
  const walls: WallBox[] = [];
  const pushWall = (cx: number, cz: number, along: "x" | "z", len: number, baseH: number): void => {
    if (len < 0.05) return;
    walls.push({ cx, cz, along, halfLen: len / 2, halfT: WALL_T / 2, baseH: baseH - SINK_M, height: WALL_H + SINK_M });
  };

  /**
   * Emit a wall run, CUT where it would cross open ground.
   *
   * A wall is a line in the plan, but the plan overlaps: corridors may cross each other
   * (space.ts only forbids routing through a third room), so a corridor's side wall can
   * run straight across another corridor's floor and seal a legal route. So the run is
   * walked in short chunks and any chunk whose centre lands INSIDE walkable ground is
   * dropped; surviving chunks are merged back into as few boxes as possible.
   */
  const CHUNK = 1.0;
  const emitWall = (
    x0: number, z0: number, x1: number, z1: number,
    along: "x" | "z", hAt: (x: number, z: number) => number,
  ): void => {
    const dx = x1 - x0, dz = z1 - z0;
    const len = Math.hypot(dx, dz);
    if (len < 0.05) return;
    const n = Math.max(1, Math.ceil(len / CHUNK));
    const flush = (a: number, b: number): void => {
      const t = (a + b) / 2;
      const cx = x0 + dx * t, cz = z0 + dz * t;
      pushWall(cx, cz, along, len * (b - a), hAt(cx, cz));
    };
    let run = -1;
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const free = nearestSd(x0 + dx * t, z0 + dz * t) > -0.15;
      if (free) { if (run < 0) run = i; }
      else if (run >= 0) { flush(run / n, i / n); run = -1; }
    }
    if (run >= 0) flush(run / n, 1);
  };

  // Room boundaries, minus corridor mouths. A mouth is always centred on the room's
  // own centre line (the first corridor cell is axis-aligned with the room cell), so a
  // side carries at most one distinct opening however many edges leave through it.
  rooms.forEach((r, i) => {
    const h = floorH[i];
    const hx = r.w / 2 + WALL_T / 2, hz = r.d / 2 + WALL_T / 2;
    for (let side = 0; side < 4; side++) {
      const open = (mouths[i] & (1 << side)) !== 0;
      const alongX = SIDE_DZ[side] !== 0;          // ±z sides run along x
      const spanHalf = (alongX ? r.w : r.d) / 2 + WALL_T;
      const fixed = alongX ? r.z + SIDE_DZ[side] * hz : r.x + SIDE_DX[side] * hx;
      const centre = alongX ? r.x : r.z;
      const put = (lo: number, hi: number): void => {
        if (alongX) emitWall(lo, fixed, hi, fixed, "x", () => h);
        else emitWall(fixed, lo, fixed, hi, "z", () => h);
      };
      if (!open) { put(centre - spanHalf, centre + spanHalf); continue; }
      put(centre - spanHalf, centre - CORRIDOR_HALF);
      put(centre + CORRIDOR_HALF, centre + spanHalf);
    }
  });

  // Corridor sides. At a bend only the OUTER wall may run past the corner — extending
  // the inner one would reach across the turn and seal the corridor shut.
  const OUT = CORRIDOR_HALF + WALL_T;
  corrFeats.forEach((c) => {
    const segs = (c.pts.length - 2) / 2;
    const dirOf = (k: number): [number, number] => {
      const dx = c.pts[2 * k + 2] - c.pts[2 * k], dz = c.pts[2 * k + 3] - c.pts[2 * k + 1];
      const l = Math.sqrt(dx * dx + dz * dz) || 1;
      return [dx / l, dz / l];
    };
    for (let k = 0; k < segs; k++) {
      const [dx, dz] = dirOf(k);
      const ax = c.pts[2 * k], az = c.pts[2 * k + 1];
      const bx = c.pts[2 * k + 2], bz = c.pts[2 * k + 3];
      const len = Math.hypot(bx - ax, bz - az);
      if (len < 1e-6) continue;
      for (const sgn of [1, -1]) {
        const nx = -dz * sgn, nz = dx * sgn;
        // Extend past a bend only on the outside of the turn (o points outward).
        let ext0 = WALL_T, ext1 = WALL_T;
        if (k > 0) {
          const [px, pz] = dirOf(k - 1);
          const ox = px - dx, oz = pz - dz;
          ext0 = ox * nx + oz * nz > 0 ? OUT : 0;
        }
        if (k + 1 < segs) {
          const [qx, qz] = dirOf(k + 1);
          const ox = dx - qx, oz = dz - qz;
          ext1 = ox * nx + oz * nz > 0 ? OUT : 0;
        }
        const off = CORRIDOR_HALF + WALL_T / 2;
        const sx = ax - dx * ext0 + nx * off, sz = az - dz * ext0 + nz * off;
        const ex = bx + dx * ext1 + nx * off, ez = bz + dz * ext1 + nz * off;
        emitWall(sx, sz, ex, ez, Math.abs(dx) > Math.abs(dz) ? "x" : "z", (qx, qz) => {
          nearestOnPoly(c, qx, qz);
          return corrH(c);
        });
      }
    }
  });

  // ── 6. gates (closed) ───────────────────────────────────────────────────────
  // TODO(P5): corridors are allowed to CROSS (space.ts only forbids routing through a
  // third room), so a corridor that happens to cross a gated one gives a geometric
  // bypass around the barrier. Harmless today (gates are decoration until the server
  // owns progression) but it must be closed — either by forbidding crossings on gated
  // edges in `space.ts`, or by gating the crossing too.
  const gates: RuinGate[] = [];
  layout.edges.forEach((e, ei) => {
    if (e.kind !== "gate") return;
    const c = corrFeats[ei];
    const target = c.len / 2;
    let seg = 0;
    while (seg < c.cum.length - 2 && c.cum[seg + 1] < target) seg++;
    const t = (target - c.cum[seg]) / Math.max(1e-6, c.cum[seg + 1] - c.cum[seg]);
    const ax = c.pts[2 * seg], az = c.pts[2 * seg + 1];
    const bx = c.pts[2 * seg + 2], bz = c.pts[2 * seg + 3];
    const cx = ax + (bx - ax) * t, cz = az + (bz - az) * t;
    const along: "x" | "z" = Math.abs(bx - ax) > Math.abs(bz - az) ? "z" : "x"; // across travel
    nearestOnPoly(c, cx, cz);
    const h = corrH(c);
    gates.push({
      edgeIndex: ei,
      open: false,
      box: { cx, cz, along, halfLen: CORRIDOR_HALF + WALL_T, halfT: 0.35, baseH: h - SINK_M, height: GATE_H + SINK_M },
    });
  });

  // ── 7. collider field ───────────────────────────────────────────────────────
  /** Planet-local point at pad-local (x, z) and ABSOLUTE height `h` above the sphere. */
  const local = (x: number, z: number, h: number): [number, number, number] => {
    const rr = R + h;
    return [x, Math.sqrt(Math.max(0, rr * rr - x * x - z * z)), z];
  };
  const toSpec = (b: WallBox): BoxSpec => {
    const base = local(b.cx, b.cz, b.baseH);
    const l = Math.hypot(base[0], base[1], base[2]) || 1;
    const up: [number, number, number] = [base[0] / l, base[1] / l, base[2] / l];
    const axis: [number, number, number] = b.along === "x" ? [1, 0, 0] : [0, 0, 1];
    const dot = axis[0] * up[0] + axis[1] * up[1] + axis[2] * up[2];
    const wx = axis[0] - up[0] * dot, wy = axis[1] - up[1] * dot, wz = axis[2] - up[2] * dot;
    const wl = Math.hypot(wx, wy, wz) || 1;
    return {
      base,
      up,
      width: [wx / wl, wy / wl, wz / wl],
      halfWidth: b.halfLen,
      height: b.height,
      halfDepth: b.halfT,
      standable: false,
    };
  };
  const buildField = (): BoxColliderField => {
    const f = new BoxColliderField(R);
    for (const w of walls) f.addBox(toSpec(w));
    for (const g of gates) if (!g.open) f.addBox(toSpec(g.box));
    return f;
  };

  // ── 8. visuals ──────────────────────────────────────────────────────────────
  const group = new THREE.Group();
  group.name = "cacheRuin";
  const disposables: { dispose(): void }[] = [];
  const gateMeshes = new Map<number, THREE.Object3D>();
  if (opts.visuals !== false) {
    buildVisuals(layout, group, disposables, gateMeshes, { sphereY, heightAt, walls, gates }, opts, R);
  }

  const dressing: RuinDressing = {
    layout,
    radius: R,
    heightAt,
    sphereY,
    colliders: buildField(),
    group,
    walls,
    gates,
    floorH: Array.from(floorH),
    setGateOpen(edgeIndex: number, open: boolean): BoxColliderField {
      const g = gates.find((x) => x.edgeIndex === edgeIndex);
      if (g === undefined) throw new Error(`setGateOpen: edge ${edgeIndex} is not a gate`);
      g.open = open;
      const mesh = gateMeshes.get(edgeIndex);
      if (mesh) mesh.visible = !open;
      dressing.colliders = buildField();
      return dressing.colliders;
    },
    dispose(): void {
      for (const d of disposables) d.dispose();
      disposables.length = 0;
      group.clear();
    },
  };
  return dressing;
}

// ── visuals ───────────────────────────────────────────────────────────────────

interface VisualCtx {
  sphereY(x: number, z: number): number;
  heightAt(x: number, z: number): number;
  walls: readonly WallBox[];
  gates: readonly RuinGate[];
}

/**
 * Silhouettes, not art: a displaced terrain plane, one instanced box per collider (so
 * the picture and the collision can never disagree), tinted floor slabs, gate slabs and
 * a distinct marker per prop kind. Everything sits on `sphereY`.
 */
function buildVisuals(
  layout: CacheLayoutV1,
  group: THREE.Group,
  disposables: { dispose(): void }[],
  gateMeshes: Map<number, THREE.Object3D>,
  ctx: VisualCtx,
  opts: RuinOptions,
  R: number,
): void {
  const keep = <T extends { dispose(): void }>(o: T): T => { disposables.push(o); return o; };

  // LIVE: the host Planet owns the ground, so the heightfield terrain is replaced by a
  // flat backdrop plate seated LIVE_UNDERPLATE_M below the datum — under the planet's
  // own relief everywhere, so it fills the void while the quadtree streams and then
  // disappears beneath it. Flat rather than the heightfield because ruin's ridges rise
  // 7 m and would punch straight up through the real ground.
  if (opts.flat === true) {
    const size = 2 * layout.extent + 90;
    const geo = keep(new THREE.PlaneGeometry(size, size, 24, 24));
    geo.rotateX(-Math.PI / 2);
    const pos = geo.attributes.position;
    const rr = R - LIVE_UNDERPLATE_M;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), z = pos.getZ(i);
      pos.setY(i, Math.sqrt(Math.max(0, rr * rr - x * x - z * z)) - R);
    }
    geo.computeVertexNormals();
    const mat = keep(new THREE.MeshStandardMaterial({ color: 0x4a4136, roughness: 1, flatShading: true }));
    const plate = new THREE.Mesh(geo, mat);
    plate.name = "cacheUnderplate";
    group.add(plate);
  }
  // Terrain — the displaced plane sharing the true sphere surface exactly.
  if (opts.flat !== true) {
    // A generous apron past the layout so the mesa's own edge is never in shot.
    const size = 2 * layout.extent + 90;
    // ⚠ CAP = FIDELITY BOUND. Mirrors `addGroundMesh` in common.ts (this file is the
    // frozen extraction-by-copy) — see the ⚠ there: 224 stretched to 1.56 m spacing at
    // the 2026-08-02 extent and the 5 cm feet-vs-visual gate read 203 mm, worst of the
    // three themes because ruin's ridge climbs 7 m over a 4.5 m run.
    const seg = Math.max(48, Math.min(320, Math.round(size / 1.2)));
    const geo = keep(new THREE.PlaneGeometry(size, size, seg, seg));
    geo.rotateX(-Math.PI / 2);
    const pos = geo.attributes.position;
    for (let i = 0; i < pos.count; i++) pos.setY(i, ctx.sphereY(pos.getX(i), pos.getZ(i)));
    geo.computeVertexNormals();
    const mat = keep(new THREE.MeshStandardMaterial({ color: 0x4a4136, roughness: 0.98, flatShading: true }));
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = "cacheGroundMesh"; // probe seam: feet-vs-visual-ground checks
    group.add(mesh);
  }

  // Room floor slabs, tinted by role.
  if (layout.rooms.length > 0) {
    const geo = keep(new THREE.BoxGeometry(1, 0.08, 1));
    const mat = keep(new THREE.MeshStandardMaterial({ roughness: 0.9 }));
    const inst = new THREE.InstancedMesh(geo, mat, layout.rooms.length);
    const m = new THREE.Matrix4();
    const col = new THREE.Color();
    layout.rooms.forEach((r, i) => {
      m.makeScale(r.w, 1, r.d);
      m.setPosition(r.x, ctx.sphereY(r.x, r.z) + 0.05, r.z);
      inst.setMatrixAt(i, m);
      inst.setColorAt(i, col.setHex(ROLE_COLOR[r.role]));
    });
    inst.instanceMatrix.needsUpdate = true;
    inst.name = "cacheRoomFloors";
    group.add(inst);
  }

  // Walls — one instance per collider box.
  if (ctx.walls.length > 0) {
    const geo = keep(new THREE.BoxGeometry(1, 1, 1));
    const mat = keep(new THREE.MeshStandardMaterial({ color: 0x8d8578, roughness: 0.95, flatShading: true }));
    const inst = new THREE.InstancedMesh(geo, mat, ctx.walls.length);
    const m = new THREE.Matrix4();
    ctx.walls.forEach((w, i) => {
      const sx = w.along === "x" ? w.halfLen * 2 : w.halfT * 2;
      const sz = w.along === "x" ? w.halfT * 2 : w.halfLen * 2;
      m.makeScale(sx, w.height, sz);
      m.setPosition(w.cx, ctx.sphereY(w.cx, w.cz) + w.baseH - ctx.heightAt(w.cx, w.cz) + w.height / 2, w.cz);
      inst.setMatrixAt(i, m);
    });
    inst.instanceMatrix.needsUpdate = true;
    inst.name = "cacheWalls";
    group.add(inst);
  }

  // Gates — individual meshes so `setGateOpen` can hide one.
  {
    const geo = keep(new THREE.BoxGeometry(1, 1, 1));
    const mat = keep(new THREE.MeshStandardMaterial({ color: 0xd9a441, roughness: 0.5, metalness: 0.4 }));
    for (const g of ctx.gates) {
      const b = g.box;
      const mesh = new THREE.Mesh(geo, mat);
      mesh.scale.set(b.along === "x" ? b.halfLen * 2 : b.halfT * 2, b.height, b.along === "x" ? b.halfT * 2 : b.halfLen * 2);
      mesh.position.set(b.cx, ctx.sphereY(b.cx, b.cz) + b.baseH - ctx.heightAt(b.cx, b.cz) + b.height / 2, b.cz);
      mesh.visible = !g.open;
      mesh.name = `cacheGate${g.edgeIndex}`;
      group.add(mesh);
      gateMeshes.set(g.edgeIndex, mesh);
    }
  }

  // Props — one simple shape per abstract kind.
  {
    const geos: Record<CachePropKind, THREE.BufferGeometry> = {
      loot: keep(new THREE.OctahedronGeometry(0.55)),
      light: keep(new THREE.SphereGeometry(0.34, 10, 8)),
      cover: keep(new THREE.BoxGeometry(1.7, 1.1, 1.1)),
      hazard: keep(new THREE.ConeGeometry(0.7, 1.3, 6)),
      marker: keep(new THREE.CylinderGeometry(0.14, 0.14, 2.4, 6)),
    };
    const mats: Record<CachePropKind, THREE.Material> = {
      loot: keep(new THREE.MeshStandardMaterial({ color: 0xe8c454, roughness: 0.3, metalness: 0.6 })),
      light: keep(new THREE.MeshStandardMaterial({ color: 0xfff0c0, emissive: 0xffcf80, emissiveIntensity: 1.2 })),
      cover: keep(new THREE.MeshStandardMaterial({ color: 0x6e6559, roughness: 1 })),
      hazard: keep(new THREE.MeshStandardMaterial({ color: 0xc0452f, roughness: 0.8 })),
      marker: keep(new THREE.MeshStandardMaterial({ color: 0xdfe6ee, roughness: 0.7 })),
    };
    const lift: Record<CachePropKind, number> = { loot: 0.6, light: 1.9, cover: 0.6, hazard: 0.7, marker: 1.2 };
    for (const p of layout.props) {
      const room = layout.rooms[p.room];
      const x = room.x + p.u, z = room.z + p.v;
      const mesh = new THREE.Mesh(geos[p.kind], mats[p.kind]);
      mesh.position.set(x, ctx.sphereY(x, z) + lift[p.kind], z);
      mesh.rotation.y = p.yaw;
      group.add(mesh);
    }
  }

  // Entrance + boss markers — you must be able to see where you came in and where the
  // fight is from anywhere on the pad.
  {
    const ent = layout.rooms[layout.entrance];
    const geo = keep(new THREE.CylinderGeometry(0.45, 0.7, 9, 8));
    const mat = keep(new THREE.MeshStandardMaterial({ color: 0x5fd0f0, emissive: 0x1d6b86, emissiveIntensity: 0.8, roughness: 0.5 }));
    const pillar = new THREE.Mesh(geo, mat);
    pillar.position.set(ent.x, ctx.sphereY(ent.x, ent.z) + 4.5, ent.z);
    pillar.name = "cacheEntranceMarker";
    group.add(pillar);
  }
  {
    const boss = layout.rooms[layout.boss];
    const geo = keep(new THREE.BoxGeometry(1.1, 7, 1.1));
    const mat = keep(new THREE.MeshStandardMaterial({ color: 0x8e2f2f, emissive: 0x3a0c0c, emissiveIntensity: 0.7, roughness: 0.85 }));
    const ring = new THREE.Group();
    const q = 0.42;
    for (const [sx, sz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
      const x = boss.x + sx * boss.w * q, z = boss.z + sz * boss.d * q;
      const pillar = new THREE.Mesh(geo, mat);
      pillar.position.set(x, ctx.sphereY(x, z) + 3.5, z);
      ring.add(pillar);
    }
    ring.name = "cacheBossMarker";
    group.add(ring);
  }
}

/** Role tint, exported for the lab's mission-graph overlay. */
export function roleColor(role: CacheRoom["role"]): number {
  return ROLE_COLOR[role];
}
